import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  mvpApiListJobs,
  JobStatus,
  JobType,
  type JobSchema,
  type MvpApiListJobsData,
} from "@augmodo/lebron-browser-client";
import { useAppSelector } from "@/store/hooks";

// grouped_by_ref isn't in the generated client's query type yet (it ships on
// the backend ahead of a client regen against the deployed OpenAPI spec);
// atlas_retailer_id is a pre-existing gap in the same generated type. Narrow
// local override so everything else still gets real type-checking.
export type ListJobsQuery = NonNullable<MvpApiListJobsData["query"]> & {
  atlas_retailer_id?: string;
  grouped_by_ref?: string;
  servable_to_me?: boolean;
};

type ListJobsResponse = { data?: { jobs?: JobSchema[] } };
type FetchJobsFn = (query: ListJobsQuery) => Promise<ListJobsResponse>;

/**
 * The request query for play mode's "fetch next job" call: today's filters
 * (type/status/retailer), explicitly ordered oldest-first -- Job.Meta.ordering
 * defaults to -created_at, which is what caused the original newest-first
 * bug -- plus optionally grouped_by_ref to prefer a job in the same
 * planogram bucket.
 *
 * servable_to_me drops jobs holding a task this reviewer must not be served,
 * currently the other half of a double-blind pair. Without it lebron still
 * refuses the task, and play mode loops on the job it keeps being offered.
 */
export function buildNextJobQuery({
  jobType,
  selectedRetailerId,
  groupedByRef,
}: {
  jobType: JobType;
  selectedRetailerId?: string | null;
  groupedByRef?: string;
}): ListJobsQuery {
  return {
    type: jobType,
    status: [JobStatus.NEEDS_REVIEW],
    page_size: 10,
    page: 1,
    ordering: "created_at",
    servable_to_me: true,
    ...(selectedRetailerId ? { atlas_retailer_id: selectedRetailerId } : {}),
    ...(groupedByRef ? { grouped_by_ref: groupedByRef } : {}),
  };
}

/** First job in the response that isn't the one the caller just finished. */
export function selectNextReviewJob(
  jobs: JobSchema[] | undefined,
  currentJobId: string | undefined,
): JobSchema | undefined {
  return jobs?.find((j) => j.id !== currentJobId);
}

/**
 * Picks the job play mode should advance to: prefer one in the same
 * planogram bucket (groupedByRef), falling back to a plain NEEDS_REVIEW
 * search if that comes back with nothing usable -- most often because the
 * bucket only contained the job just finished, which selectNextReviewJob
 * filters out. `fetchJobs` is injected so this can be tested without a
 * network call or a generated-client mock.
 */
export async function fetchNextReviewJob(
  fetchJobs: FetchJobsFn,
  {
    jobType,
    selectedRetailerId,
    groupedByRef,
    currentJobId,
  }: {
    jobType: JobType;
    selectedRetailerId?: string | null;
    groupedByRef?: string;
    currentJobId: string | undefined;
  },
): Promise<JobSchema | undefined> {
  const tryFetch = async (ref?: string) => {
    const response = await fetchJobs(
      buildNextJobQuery({ jobType, selectedRetailerId, groupedByRef: ref }),
    );
    return selectNextReviewJob(response.data?.jobs, currentJobId);
  };

  if (groupedByRef) {
    const prioritized = await tryFetch(groupedByRef);
    if (prioritized) return prioritized;
  }
  return tryFetch();
}

interface UseFetchNextPlayModeJobArgs {
  /** Only fires the request while true (caller owns all its own gating). */
  enabled: boolean;
  jobId: string | undefined;
  jobType: JobType;
  route: string;
  /**
   * Best-known planogram id for the task currently on screen, recomputed
   * every render. May go stale/undefined once the finished task's data is
   * cleared from cache -- that's expected; the hook remembers the last
   * non-empty value it saw and uses that instead.
   */
  livePlanogramId: string | undefined | null;
  /** Called when no eligible next job is found, or the request fails. */
  onNoJobFound: () => void;
}

export function useFetchNextPlayModeJob({
  enabled,
  jobId,
  jobType,
  route,
  livePlanogramId,
  onNoJobFound,
}: UseFetchNextPlayModeJobArgs) {
  const navigate = useNavigate();
  const selectedRetailerId = useAppSelector(
    (state) => state.oos.selectedRetailerId,
  );

  const lastPlanogramIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (livePlanogramId) lastPlanogramIdRef.current = livePlanogramId;
  }, [livePlanogramId]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    fetchNextReviewJob((query) => mvpApiListJobs({ query }), {
      jobType,
      selectedRetailerId,
      groupedByRef: lastPlanogramIdRef.current,
      currentJobId: jobId,
    })
      .then((nextJob) => {
        if (cancelled) return;
        if (nextJob) navigate(`/jobs/${nextJob.id}/${route}`);
        else onNoJobFound();
      })
      .catch(() => {
        if (!cancelled) onNoJobFound();
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    jobId,
    jobType,
    route,
    selectedRetailerId,
    navigate,
    onNoJobFound,
  ]);
}
